// Tile layer
import { ClipExtension } from '@deck.gl/extensions';
import { TileLayer } from '@deck.gl/geo-layers';
import { BitmapLayer } from '@deck.gl/layers';
import * as _ from 'lodash';
import queryString from 'query-string';

import CustomColorMaps from '@src/components/Colormaps/Assets/Custom_colormaps/Descrete_colors.json';
import renderSettings from '@src/containers/Viewer/subComponents/RenderRaster/options/renderSettings.json';
import {
  setLeftColorScale,
  setLeftUrl,
  setRightColorScale,
  setRightUrl
} from '@src/redux/_reducers/projectReducer';
import store from '@src/redux/store';

// import { maxAreaReachedError } from './AoiUtils';
import { customFetch } from './CustomFetch';
import { Indices, RenderOptions, colormaps } from './IndiceUtils';
import { COLORMAP_TYPES, RESAMPLING_METHODS } from './constants/APP';
import {
  ASSET_TYPE_STRINGS,
  VISUALIZATION_EXPRESSION_TYPES
} from './constants/ATLAS_CONSTANTS';
import { CONSTELLATION_IDS_CONSTS } from './constants/LENS_CONSTANTS';

export const isStacAsset = assetType =>
  assetType === ASSET_TYPE_STRINGS.stac_item;

export function isSentinel1(activeAoi) {
  return ['Sentinel-1', CONSTELLATION_IDS_CONSTS.SENTINEL_1_RTC].includes(
    activeAoi.lens_satellite_id || activeAoi.satellite_name
  );
}

function isAllowCustomRgbBands(activeAoi) {
  const { satellite_name } = activeAoi;

  return (
    satellite_name === CONSTELLATION_IDS_CONSTS.PIXXEL_D2 ||
    satellite_name === CONSTELLATION_IDS_CONSTS.SHAKUNTALA
  );
}

function getBidx(activeAoi) {
  const activeAoiCheck = !_.isEmpty(activeAoi);

  if (!activeAoiCheck) return [];

  if (isAllowCustomRgbBands(activeAoi)) {
    const RGBCombination = activeAoi.rgb_bands;
    const rgbIndexes = Object.values(RGBCombination).map(
      color => activeAoi.eo_bands.indexOf(color) + 1
    );

    return rgbIndexes;
  }

  const RGBCombination = activeAoi.metadata.composite;
  const rgbIndexes = Object.values(RGBCombination).map(
    color => activeAoi.eo_bands.indexOf(color) + 1
  );

  return rgbIndexes.reverse();
}

const getCogParams = (activeAoi, url) => {
  const activeAoiCheck = !_.isEmpty(activeAoi);
  const rescale =
    (activeAoiCheck && activeAoi?.metadata?.rescale?.join(`%2C`)) ?? '';

  const satellite_name =
    activeAoiCheck && (activeAoi.lens_satellite_id || activeAoi.satellite_name);

  const COMMON_PARAMS = Object.freeze({
    url,
    resampling_method: RESAMPLING_METHODS.nearestResampling,
    bidx: getBidx(activeAoi)
  });

  const CUSTOM_PARAMS_MAP = Object.freeze({
    'Sentinel-1 (RTC)': {
      ...COMMON_PARAMS,
      rescale: ['0%2C.8000', '0%2C1.000'],
      bidx: [2, 1],
      expression: `0.03+%2B+log+(10e-4+-+log+(0.05+%2F+(0.02+%2B+2+*+b2)))%3B0.05+%2B+exp+(0.25+*+(log+(0.01+%2B+2+*+b2)+%2B+log+(0.02+%2B+5+*+b1)))%3B1+-+log+(0.05+%2F+(0.045+-+0.9+*+b2))`
    },
    [CONSTELLATION_IDS_CONSTS.SENTINEL_1_RTC]: {
      ...COMMON_PARAMS,
      rescale: ['0%2C.8000', '0%2C1.000'],
      bidx: [2, 1],
      expression: `0.03+%2B+log+(10e-4+-+log+(0.05+%2F+(0.02+%2B+2+*+b2)))%3B0.05+%2B+exp+(0.25+*+(log+(0.01+%2B+2+*+b2)+%2B+log+(0.02+%2B+5+*+b1)))%3B1+-+log+(0.05+%2F+(0.045+-+0.9+*+b2))`
    },
    'MODIS Surface Reflectance 8-Day': {
      ...COMMON_PARAMS,
      color_formula: `gamma+RGB+3.0%2C+saturation+1.9%2C+sigmoidal+RGB+0+0.55`
    },
    [CONSTELLATION_IDS_CONSTS.MODIS_SURFACE_REFLECTANCE_8_DAY]: {
      ...COMMON_PARAMS,
      color_formula: `gamma+RGB+3.0%2C+saturation+1.9%2C+sigmoidal+RGB+0+0.55`
    },
    default: { ...COMMON_PARAMS, rescale }
  });

  return CUSTOM_PARAMS_MAP[satellite_name] ?? CUSTOM_PARAMS_MAP['default'];
};

/**
 *
 * @param {String} s3URL - atlas asset s3 URL
 * @param {Object} activeAoi - activeAoi object from redux
 * @param {String} format - global tileFormat from redux
 * @returns
 */
export const CogRasterUrlHandler = (
  s3URL,
  activeAoi,
  format,
  queryParamStr = ''
) => {
  return `${
    import.meta.env.VITE_COG_TILER
  }/cog/tiles/WebMercatorQuad/{z}/{x}/{y}.${format}?${queryString.stringify(
    queryParamStr || getCogParams(activeAoi, s3URL),
    { ...RenderOptions }
  )}`;
};
/**
 *
 * @param {String} url - atlas asset s3 URL
 * @param {Object} activeAoi - activeAoi object from redux
 * @param {String} format - global tileFormat from redux
 * @returns
 */
export const StacRasterUrlHandler = (
  url,
  activeAoi,
  format,
  queryParamStr = ''
) => {
  return `${
    import.meta.env.VITE_STAC_TILER
  }/stac/tiles/WebMercatorQuad/{z}/{x}/{y}.${format}?url=${
    url
  }${queryParamStr || activeAoi.metadata.preview_url}`;
};

/**
 *
 * @param {String} url - atlas asset s3 URL
 * @param {Object} activeAoi - activeAoi object from redux
 * @param {String} format - global tileFormat from redux
 * @param {String} assetType - type of path link - stac or cog
 * @returns
 */
export const InputRasterUrlHandler = ({
  url,
  activeAoi,
  assetType,
  format,
  queryParamStr = ''
}) => {
  if (!url) return '';

  if (isStacAsset(assetType)) {
    return StacRasterUrlHandler(url, activeAoi, format, queryParamStr);
  }
  return CogRasterUrlHandler(url, activeAoi, format, queryParamStr);
};

/**
 *
 * @param {String} s3URL - atlas asset s3 URL
 * @param {String} previewUrl - optional param, preview url from meta data of aoi
 * @returns
 */
export const InputRasterStacThumbnailHandler = (
  s3URL,
  previewUrl = '',
  format = 'jpg'
) => {
  if (!s3URL) return '';
  return `${import.meta.env.VITE_STAC_TILER}/stac/preview.${format}?url=${s3URL}&max_size=200${previewUrl}`;
};

/**
 *
 * @param {String} s3URL - atlas asset s3 URL
 * @param {*} activeAOI - active aoi from redux
 * @returns
 */
export const InputRasterCogThumbnailHandler = (s3URL, activeAoi) => {
  if (!s3URL) return '';
  return `${
    import.meta.env.VITE_COG_TILER
  }/cog/preview.jpg?${queryString.stringify(
    { ...getCogParams(activeAoi, s3URL), max_size: 200 },
    { ...RenderOptions }
  )}`;
};

/**
 *
 * @param {string} url - path to the asset
 * @param {string} assetType  - type of asset
 * @param {*} activeAoi - activeAoi from redux
 * @returns
 */
export const InputRasterThumbnailHandler = ({ url, activeAoi, assetType }) => {
  if (!url) return '';

  if (isStacAsset(assetType)) {
    return InputRasterStacThumbnailHandler(url, activeAoi.metadata.preview_url);
  }
  return InputRasterCogThumbnailHandler(url, activeAoi);
};

export const createCosmosS3Url = ({ imageId, collectionId }) => {
  if (!imageId || !collectionId) return '';
  return `http://cosmos.platform.svc.cluster.local/api/v1/collections/${collectionId}/items/${imageId}`;
};

export const createStacCosmosUrl = ({
  imageId,
  collectionId,
  previewUrl = ''
}) => {
  const cosmosUrl = createCosmosS3Url({ imageId, collectionId });
  if (!cosmosUrl) return '';

  const format = store.getState().sourceActions.tileRenderFormat;

  return `${import.meta.env.VITE_STAC_TILER}/stac/tiles/WebMercatorQuad/{z}/{x}/{y}.${format}?url=${cosmosUrl}${previewUrl}`;
};

/**
 *
 * @param {*} key - react unique key
 * @param {*} id - unique id for DeckGL layer
 * @param {string} url - url from which raster needs to render
 * @param {Array} clipBounds - OPTIONAL: if passed needs to be bounds,
 * eg: [-102.56798314716013, 34.12100618631839, -102.14120098289239, 34.425361135549515]
 * @param {boolean} isReactNode - OPTIONAL: if passed as false, then will return
 * @param {number[]} opacity - to set opacity of tilelayer, default is 1
 * class member of TileLayer from DeckGL
 * @returns - Single ReactNode/CLass member of TileLayer from DeckGL, based on
 * isReactNode param
 */
export const ReactTileLayer = (
  key,
  id,
  url,
  clipBounds = [],
  isReactNode = true,
  opacity = [1]
) => {
  const clipProps =
    clipBounds.length > 0
      ? {
          extensions: [new ClipExtension()],
          clipBounds: clipBounds
        }
      : null;

  const props = {
    key: key,
    id: id,
    data: url,
    minZoom: 3,
    maxZoom: 19,
    tileSize: 256,
    fetch: customFetch.fetchTile,
    opacity: opacity,
    extent: clipBounds.length > 0 ? clipBounds : null,
    renderSubLayers: props => {
      const {
        bbox: { west, south, east, north }
      } = props.tile;
      return new BitmapLayer(props, {
        data: null,
        image: props.data,
        bounds: [west, south, east, north]
      });
    },
    ...clipProps,
    onTileError: () => {
      return null;
    }
  };
  return isReactNode ? <TileLayer {...props} /> : new TileLayer({ ...props });
};

export const getVisAssetUrl = ({ params = {}, exp = '', assetPath, isHSI }) => {
  const format = store.getState().sourceActions.tileRenderFormat;
  const urlParams =
    '&' +
    `${queryString.stringify(params, {
      ...RenderOptions
    })}${exp}`;

  if (isHSI || !assetPath) return urlParams;

  return `${
    import.meta.env.VITE_STAC_TILER
  }/stac/tiles/WebMercatorQuad/{z}/{x}/{y}.${format}?url=${
    assetPath
  }${urlParams}`;
};

//generate indice url
export function CustomIndiceURLHandler(
  formula,
  rescaleValue,
  side,
  updateColorScale,
  activeAoi,
  assetPath
) {
  const splitMode = store.getState().projects.visualize.splitMode;
  const isHSI = activeAoi?.metadata?.isHyperSpectral;

  let renderParams = {
    expression: encodeURIComponent(formula),
    rescale: `${rescaleValue[0]}%2C${rescaleValue[1]}`,
    colormap_name: 'spectral'
  };

  renderParams = {
    ...renderParams,
    asset_as_band: true,
    unscale: true,
    resampling_method: renderSettings.resampling_method
  };

  const tilerURL = getVisAssetUrl({
    params: renderParams,
    assetPath,
    isHSI
  });

  const rescaleArray = rescaleValue.map(Number);

  const colorMapObject = {
    rescale: [...rescaleArray],
    colormap: 'spectral',
    type: COLORMAP_TYPES.rangeColormap,
    buckets: {},
    title: ''
  };
  updateColorScale(colorMapObject);

  if (splitMode) {
    if (side === 'right') {
      store.dispatch(setRightUrl(tilerURL));
      store.dispatch(setRightColorScale(colorMapObject));
      return;
    }
    if (side === 'left') {
      store.dispatch(setLeftUrl(tilerURL));
      store.dispatch(setLeftColorScale(colorMapObject));
      return;
    }
  }
  return tilerURL;
}

// generate composite url
/**
 *
 * @param {Object} composite - selected composite map with rgb mapped to satellite band asset name
 * @returns {String} - titiler req params stringified
 */
export const CompositeUrlHandler = (
  composite,
  activeAoi,
  side,
  inputRescale = null,
  assetPath,
  assetType,
  bandsInfo
) => {
  const activeAoiCheck = !_.isEmpty(activeAoi);
  const splitMode = store.getState().projects.visualize.splitMode;
  const AOIRescale = activeAoi.metadata?.rescale;
  const isHSI = activeAoi?.metadata?.isHyperSpectral;
  let min = Infinity,
    max = -Infinity;

  const rescale =
    inputRescale !== null
      ? `${inputRescale[0]}%2C${inputRescale[1]}`
      : AOIRescale
        ? `${AOIRescale[0]}%2C${AOIRescale[1]}`
        : '';

  let asset = '';
  Object.keys(composite).map(color => {
    activeAoiCheck &&
      activeAoi.eo_bands.map(b => {
        if (b === composite[color]) {
          asset += `&assets=${b}`;
          if (bandsInfo[b]) {
            min = Math.min(min, bandsInfo[b].statistics.minimum);
            max = Math.max(max, bandsInfo[b].statistics.maximum);
          }
        }
      });
  });

  const COMMON_PARAMS = {
    rescale: rescale,
    nodata: 0
  };

  const params = {
    ...COMMON_PARAMS,
    rescale: isHSI ? `${min},${max}` : rescale,
    resampling_method: renderSettings.resampling_method
  };

  const tilerURL = getVisAssetUrl({
    params,
    exp: asset,
    assetPath,
    isHSI
  });

  if (splitMode) {
    if (side === 'right') {
      store.dispatch(setRightUrl(tilerURL));
      return;
    }

    if (side === 'left') {
      store.dispatch(setLeftUrl(tilerURL));
      return;
    }
  }
  return tilerURL;
};

export function getLwir11Rescale() {
  // https://developers.google.com/earth-engine/datasets/catalog/landsat
  return '1,65535';
}

export function SingleBandUrlHandler(
  band,
  activeAoi,
  side,
  inputRescale = null,
  assetPath,
  assetType,
  bandsInfo
) {
  let renderParams = {};
  const splitMode = store.getState().projects.visualize.splitMode;
  const AOIRescale = activeAoi.metadata?.rescale;
  const isHSI = activeAoi.metadata?.isHyperSpectral;

  const rescale =
    band === 'lwir11'
      ? getLwir11Rescale()
      : inputRescale !== null
        ? `${inputRescale[0]}%2C${inputRescale[1]}`
        : AOIRescale
          ? `${AOIRescale[0]}%2C${AOIRescale[1]}`
          : '';

  if (band === 'SCL') {
    renderParams = {
      colormap: CustomColorMaps.SCL,
      colormap_type: 'linear',
      nodata: 0,
      assets: band
    };
  } else {
    renderParams = {
      rescale: rescale,
      nodata: 0,
      asset_as_band: true,
      resampling_method: renderSettings.resampling_method,
      assets: band
    };
  }

  if (bandsInfo?.[band] && band !== 'SCL') {
    renderParams.rescale = isHSI
      ? `${bandsInfo[band].statistics.minimum},${bandsInfo[band].statistics.maximum}`
      : rescale;
  }

  if (isSentinel1(activeAoi)) {
    delete renderParams.nodata;
  }

  const tilerURL = getVisAssetUrl({
    params: renderParams,
    assetPath,
    isHSI
  });

  // return tilerURL;
  if (splitMode) {
    if (side === 'right') {
      store.dispatch(setRightUrl(tilerURL));
      // store.dispatch(setRightColorScale("bands"));
      return;
    }

    if (side === 'left') {
      store.dispatch(setLeftUrl(tilerURL));
      // store.dispatch(setLeftColorScale("bands"));
      return;
    }
  }
  return tilerURL;
}

export const getCogURLIndices = (url, _, format, assets = []) => {
  const selectedAsset = assets.find(i => i.path === url);

  if (!selectedAsset) return '';

  const params = {
    url: url,
    rescale: selectedAsset.rescale.join(','),
    colormap_name: selectedAsset.colormap,
    resampling_method: 'nearest'
  };

  const cog_url = `${
    import.meta.env.VITE_COG_TILER
  }/cog/tiles/WebMercatorQuad/{z}/{x}/{y}.${format}?${queryString.stringify(
    params,
    {
      sort: false,
      strict: false,
      encode: false
    }
  )}`;

  return cog_url;
};

export function GeneratePresetIndiceUrlHandler(
  id,
  activeAoi,
  side,
  updateColorScale,
  assetPath
) {
  const indiceMetadata = Indices.find(i => i.id === id);
  const encodedRescale = indiceMetadata?.rescale;
  const decodedRescale = decodeURIComponent(encodedRescale);
  const RescaleArr = decodedRescale.split(',').map(Number);
  const splitMode = store.getState().projects.visualize.splitMode;
  const isHSI = activeAoi.metadata?.isHyperSpectral;

  const COMMON_PARAMS = {
    expression: encodeURIComponent(
      activeAoi.metadata.indices[id.toLowerCase()]
    ),
    rescale: indiceMetadata?.rescale,
    colormap_name: colormaps[id]
  };

  let renderParams = {
    ...COMMON_PARAMS,
    asset_as_band: true,
    resampling_method: renderSettings.resampling_method,
    unscale: true
  };

  const tilerURL = getVisAssetUrl({
    params: renderParams,
    exp: '',
    assetPath,
    isHSI
  });

  const colorMapObject = {
    rescale: [...RescaleArr],
    colormap: colormaps[id],
    type: COLORMAP_TYPES.rangeColormap,
    buckets: {},
    title: ''
  };

  updateColorScale(colorMapObject);
  if (splitMode) {
    if (side === 'right') {
      store.dispatch(setRightUrl(tilerURL));
      store.dispatch(setRightColorScale(colorMapObject));
      return;
    }

    if (side === 'left') {
      store.dispatch(setLeftUrl(tilerURL));
      store.dispatch(setLeftColorScale(colorMapObject));
      return;
    }
  }
  return tilerURL;
}

//convert R = B04,G = B8A,B = B02 to
//  {
//     "Red": "B05",
//     "Green": "B03",
//     "Blue": "B02"
// }
/**
 *
 * @param {String} expression - accepts expression value from atlas, GET Custom Indice | GET Visualization by ID
 * Acceptes expression string only in  R=B04,G=B8A,B=B02
 * @returns {Object} - Composite object in {Red: B01, Green: B01, Blue: B01}
 */
const parseCompositeExpression = expression => {
  return expression.split(',').reduce((acc, pair) => {
    const [key, value] = pair.split('=').map(item => item.trim());
    acc[key === 'R' ? 'Red' : key === 'G' ? 'Green' : 'Blue'] = value;
    return acc;
  }, {});
};

//generate previewURL url when creating/viewing custom indices
export const generateCustomIndicePreviewURL = (
  expressionType,
  expression,
  activeAoi,
  rescaleValue,
  side,
  updateColorScale,
  assetPath,
  assetType,
  bandsInfo
) => {
  switch (expressionType) {
    case VISUALIZATION_EXPRESSION_TYPES.customIndex:
      return CustomIndiceURLHandler(
        expression,
        rescaleValue,
        side,
        updateColorScale,
        activeAoi,
        assetPath,
        assetType
      );
    case VISUALIZATION_EXPRESSION_TYPES.RGBComposite: {
      const parsedExpression = parseCompositeExpression(expression);
      return CompositeUrlHandler(
        parsedExpression,
        activeAoi,
        side,
        rescaleValue,
        assetPath,
        assetType,
        bandsInfo
      );
    }
    case VISUALIZATION_EXPRESSION_TYPES.singleBand:
      return SingleBandUrlHandler(
        expression,
        activeAoi,
        side,
        rescaleValue,
        assetPath,
        assetType,
        bandsInfo
      );
    default:
      return null;
  }
};

export function generateVizViewUrl(
  {
    index_name,
    path,
    additionalRenderParams = {},
    additionalColorMapParams = {}
  },
  updateColorScale
) {
  const rescale = Indices.find(i => i.id === index_name)?.rescale;
  const colormap = colormaps[index_name];

  const format = store.getState().sourceActions.tileRenderFormat;

  const renderParams = {
    url: path,
    rescale: rescale,
    colormap_name: colormap,
    ...additionalRenderParams
  };

  const colorMapObject = {
    rescale: rescale?.split('%2C').map(Number),
    colormap: colormap,
    type: COLORMAP_TYPES.rangeColormap,
    buckets: {},
    title: '',
    ...additionalColorMapParams
  };

  if (colorMapObject.colormap) updateColorScale(colorMapObject);

  const tilerURL = `${
    import.meta.env.VITE_COG_TILER
  }/cog/tiles/WebMercatorQuad/{z}/{x}/{y}.${format}?${queryString.stringify(
    renderParams,
    { ...RenderOptions }
  )}`;

  return tilerURL;
}

export const clearSplitConfigStates = side => {
  if (side === 'right') {
    store.dispatch(setRightUrl(''));
    store.dispatch(setRightColorScale({}));
  } else if (side === 'left') {
    store.dispatch(setLeftUrl(''));
    store.dispatch(setLeftColorScale({}));
  }
};
